import React, { useState, useEffect, useRef } from 'react';
import { useCheckout } from '../CheckoutContext';

const CODE_LINES = [
  '#!/usr/bin/env openroom',
  'import { OpenBro, Elite } from "@openroom/core";',
  'import { Musculation } from "@openroom/discipline";',
  'import { AI, Automation } from "@openroom/tech";',
  'import { Revenue } from "@openroom/ecommerce";',
  '',
  '// [VIP] Initialisation protocole BOSS...',
  'const config = {',
  '  access: "LIFETIME_VIP",',
  '  clearance: "TOP_SECRET",',
  '  formations: 6,',
  '  mentors: "agents_secrets",',
  '  communaute: 1500,',
  '};',
  '',
  'const boss = await OpenBro.deploy(config);',
  '',
  '// Chargement des modules...',
  'await boss.load("Musculation & Discipline");',
  'await boss.load("Reseaux Sociaux & E-commerce");',
  'await boss.load("Creation de Contenu Video");',
  'await boss.load("Montage Video Pro");',
  'await boss.load("IA & Automatisation");',
  'await boss.load("Nutrition & Performance");',
  '',
  '// [VIP] Activation privileges...',
  'boss.mindset = "ELITE";',
  'boss.discipline = true;',
  'boss.revenue.target = "10K/mois";',
  'boss.body.transform({ weeks: 12 });',
  '',
  'await AI.automate(boss.business);',
  'await Revenue.scale(boss.ecommerce);',
  '',
  'if (boss.ready()) {',
  '  console.log(">> ACCES VIP AUTORISE");',
  '  console.log(">> Bienvenue, Boss.");',
  '  Elite.activate(boss);',
  '}',
  '',
  '> Systeme pret.',
  '> Redirection vers le portail VIP...',
];

const PortalTransition = () => {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState(0); // 0=portal opening, 1=code typing, 2=welcome
  const [typedLines, setTypedLines] = useState([]);
  const [currentLine, setCurrentLine] = useState('');
  const [lineIndex, setLineIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const { startCheckout } = useCheckout();

  // Phase transitions
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 600);
    const t2 = setTimeout(() => setPhase(2), 4500);
    const t3 = setTimeout(() => { startCheckout(); }, 6200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [startCheckout]);

  // Typing effect
  useEffect(() => {
    if (phase !== 1) return;
    if (lineIndex >= CODE_LINES.length) return;

    const line = CODE_LINES[lineIndex];
    if (charIndex <= line.length) {
      const t = setTimeout(() => {
        setCurrentLine(line.substring(0, charIndex));
        setCharIndex((c) => c + 1);
      }, 10);
      return () => clearTimeout(t);
    } else {
      setTypedLines((prev) => [...prev, line]);
      setCurrentLine('');
      setCharIndex(0);
      setLineIndex((l) => l + 1);
    }
  }, [phase, lineIndex, charIndex]);

  // Canvas portal animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let time = 0;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    // Particles orbiting the portal
    const particles = Array.from({ length: 120 }, () => ({
      angle: Math.random() * Math.PI * 2,
      radius: 80 + Math.random() * 200,
      speed: 0.005 + Math.random() * 0.02,
      size: 1 + Math.random() * 2.5,
      alpha: 0.2 + Math.random() * 0.6,
      drift: (Math.random() - 0.5) * 0.3,
    }));

    const draw = () => {
      time += 0.016;
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;

      // Fade background
      ctx.fillStyle = 'rgba(0, 0, 0, 0.12)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Portal ring growth
      const portalProgress = Math.min(time / 1.5, 1);
      const portalRadius = portalProgress * Math.min(cx, cy) * 0.45;

      // Outer glow
      if (portalRadius > 10) {
        const grd = ctx.createRadialGradient(cx, cy, portalRadius * 0.6, cx, cy, portalRadius * 1.4);
        grd.addColorStop(0, 'rgba(0, 212, 255, 0)');
        grd.addColorStop(0.5, `rgba(0, 212, 255, ${0.03 * portalProgress})`);
        grd.addColorStop(1, 'rgba(0, 212, 255, 0)');
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Portal rings
      for (let r = 0; r < 3; r++) {
        const ringRadius = portalRadius * (0.7 + r * 0.15);
        const ringAlpha = (0.4 - r * 0.12) * portalProgress;
        ctx.beginPath();
        ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(0, 212, 255, ${ringAlpha})`;
        ctx.lineWidth = 1.5 - r * 0.3;
        ctx.stroke();
      }

      // Spinning dashes on main ring
      const dashCount = 24;
      for (let i = 0; i < dashCount; i++) {
        const a = (i / dashCount) * Math.PI * 2 + time * 0.8;
        const len = 8 + Math.sin(time * 3 + i) * 4;
        const x1 = cx + Math.cos(a) * portalRadius;
        const y1 = cy + Math.sin(a) * portalRadius;
        const x2 = cx + Math.cos(a) * (portalRadius + len);
        const y2 = cy + Math.sin(a) * (portalRadius + len);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = `rgba(0, 212, 255, ${0.3 + Math.sin(time * 2 + i) * 0.2})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Orbiting particles
      for (const p of particles) {
        p.angle += p.speed;
        const pr = p.radius * portalProgress;
        const px = cx + Math.cos(p.angle) * pr;
        const py = cy + Math.sin(p.angle) * (pr * 0.6); // elliptical
        ctx.beginPath();
        ctx.arc(px, py, p.size * portalProgress, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 212, 255, ${p.alpha * portalProgress})`;
        ctx.fill();
      }

      // Center light burst
      if (portalProgress > 0.5) {
        const burstAlpha = (portalProgress - 0.5) * 0.15;
        const grd2 = ctx.createRadialGradient(cx, cy, 0, cx, cy, portalRadius * 0.5);
        grd2.addColorStop(0, `rgba(0, 212, 255, ${burstAlpha})`);
        grd2.addColorStop(1, 'rgba(0, 212, 255, 0)');
        ctx.fillStyle = grd2;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      animationId = requestAnimationFrame(draw);
    };

    draw();
    return () => { cancelAnimationFrame(animationId); window.removeEventListener('resize', resize); };
  }, []);

  const getLineColor = (line) => {
    if (line.startsWith('#!')) return '#666666';
    if (line.startsWith('//')) return '#E6EBF2';
    if (line.startsWith('>')) return '#00FF88';
    if (line.startsWith('import')) return '#FF79C6';
    if (line.startsWith('const ') || line.startsWith('if ')) return '#BD93F9';
    if (line.startsWith('await ') || line.startsWith('  await ')) return '#FFB86C';
    if (line.includes('console.log')) return '#00FF88';
    if (line.trim().startsWith("'") || line.trim().startsWith('"')) return 'rgba(0, 212, 255, 0.7)';
    if (line.includes(': ') || line.includes('= ')) return 'rgba(255, 255, 255, 0.7)';
    return 'rgba(255, 255, 255, 0.45)';
  };

  const SYS_LOGS = [
    '[SYS] Connexion securisee etablie...',
    '[SYS] Protocole VIP active',
    '[SYS] Chiffrement AES-256 OK',
    '[NET] Tunnel prive ouvert',
    '[AUTH] Verification identite...',
    '[AUTH] Clearance: TOP SECRET',
    '[LOAD] Musculation.......... OK',
    '[LOAD] Reseaux Sociaux...... OK',
    '[LOAD] Contenu Video........ OK',
    '[LOAD] Montage Video........ OK',
    '[LOAD] IA & Automatisation.. OK',
    '[LOAD] Nutrition............ OK',
    '[SYS] 6/6 modules charges',
    '[VIP] Acces premium confirme',
    '[PORTAL] Ouverture en cours...',
    '[READY] Bienvenue, Boss.',
  ];

  const [sysLines, setSysLines] = useState([]);
  const [sysIndex, setSysIndex] = useState(0);

  useEffect(() => {
    if (phase < 1) return;
    if (sysIndex >= SYS_LOGS.length) return;
    const delay = 80 + Math.random() * 150;
    const t = setTimeout(() => {
      setSysLines((prev) => [...prev, SYS_LOGS[sysIndex]]);
      setSysIndex((i) => i + 1);
    }, delay);
    return () => clearTimeout(t);
  }, [phase, sysIndex]);

  const getSysColor = (line) => {
    if (line.includes('[AUTH]')) return '#FF79C6';
    if (line.includes('[VIP]') || line.includes('[READY]')) return '#00FF88';
    if (line.includes('OK')) return '#E6EBF2';
    if (line.includes('[LOAD]')) return 'rgba(255,255,255,0.6)';
    return 'rgba(0, 212, 255, 0.5)';
  };

  return (
    <div
      data-testid="portal-transition"
      className="fixed inset-0 z-[999999] flex items-center justify-center"
      style={{ backgroundColor: '#000000' }}
    >
      <canvas ref={canvasRef} className="absolute inset-0" />

      {/* Code terminal - left side */}
      <div
        className="absolute left-[3%] top-1/2 -translate-y-1/2 z-10 max-w-[420px] w-full hidden lg:block"
        style={{
          opacity: phase >= 1 ? 1 : 0,
          transform: phase >= 1 ? 'translateX(0)' : 'translateX(-40px)',
          transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
        }}
      >
        <div className="rounded-md overflow-hidden" style={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(0,212,255,0.2)', boxShadow: '0 0 30px rgba(0,212,255,0.08)' }}>
          <div className="flex items-center gap-1.5 px-3 py-2" style={{ background: 'rgba(0,212,255,0.06)', borderBottom: '1px solid rgba(0,212,255,0.1)' }}>
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
            <span className="ml-2 text-[10px] tracking-wider uppercase" style={{ color: 'rgba(0,212,255,0.5)', fontFamily: 'monospace' }}>openroom://main.js</span>
          </div>
          <div className="p-3 overflow-hidden" style={{ minHeight: '420px', maxHeight: '480px' }}>
            {typedLines.map((line, i) => (
              <div key={i} className="text-[11.5px] leading-[1.7] flex">
                <span className="w-[28px] text-right mr-3 select-none flex-shrink-0" style={{ color: 'rgba(255,255,255,0.15)', fontFamily: 'monospace', fontSize: '10px' }}>{i + 1}</span>
                <span style={{ fontFamily: '"SF Mono", "Fira Code", monospace', color: getLineColor(line) }}>{line || '\u00A0'}</span>
              </div>
            ))}
            {phase === 1 && lineIndex < CODE_LINES.length && (
              <div className="text-[11.5px] leading-[1.7] flex">
                <span className="w-[28px] text-right mr-3 select-none flex-shrink-0" style={{ color: 'rgba(255,255,255,0.15)', fontFamily: 'monospace', fontSize: '10px' }}>{typedLines.length + 1}</span>
                <span style={{ fontFamily: '"SF Mono", "Fira Code", monospace', color: getLineColor(currentLine || CODE_LINES[lineIndex]) }}>
                  {currentLine}<span className="inline-block w-[7px] h-[15px] ml-[1px] animate-pulse" style={{ background: '#E6EBF2', verticalAlign: 'middle' }} />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* System logs terminal - right side */}
      <div
        className="absolute right-[3%] top-1/2 -translate-y-1/2 z-10 max-w-[360px] w-full hidden lg:block"
        style={{
          opacity: phase >= 1 ? 1 : 0,
          transform: phase >= 1 ? 'translateX(0)' : 'translateX(40px)',
          transition: 'opacity 0.5s ease-out 0.3s, transform 0.5s ease-out 0.3s',
        }}
      >
        <div className="rounded-md overflow-hidden" style={{ background: 'rgba(0,0,0,0.8)', border: '1px solid rgba(0,212,255,0.2)', boxShadow: '0 0 30px rgba(0,212,255,0.08)' }}>
          <div className="flex items-center gap-1.5 px-3 py-2" style={{ background: 'rgba(0,212,255,0.06)', borderBottom: '1px solid rgba(0,212,255,0.1)' }}>
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
            <span className="ml-2 text-[10px] tracking-wider uppercase" style={{ color: 'rgba(0,212,255,0.5)', fontFamily: 'monospace' }}>openroom://system.log</span>
          </div>
          <div className="p-3 overflow-hidden" style={{ minHeight: '320px', maxHeight: '380px' }}>
            {sysLines.map((line, i) => (
              <div key={i} className="text-[11px] leading-[1.9]" style={{ fontFamily: '"SF Mono", "Fira Code", monospace', color: getSysColor(line) }}>
                <span style={{ color: 'rgba(255,255,255,0.2)' }}>{String(i).padStart(2, '0')} </span>
                {line}
              </div>
            ))}
            {phase >= 1 && sysIndex < SYS_LOGS.length && (
              <div className="text-[11px] leading-[1.9]" style={{ fontFamily: 'monospace', color: 'rgba(0,212,255,0.4)' }}>
                <span className="animate-pulse">_</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Welcome text - center */}
      <div
        className="relative z-10 text-center px-6"
        style={{
          opacity: phase >= 2 ? 1 : 0,
          transform: phase >= 2 ? 'scale(1)' : 'scale(0.7)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
        }}
      >
        <h1
          className="text-[48px] sm:text-[64px] lg:text-[80px] font-black leading-[1]"
          style={{ fontFamily: '"Space Grotesk", sans-serif', color: '#FFFFFF' }}
        >
          Welcome to
        </h1>
        <h1
          className="text-[52px] sm:text-[72px] lg:text-[96px] font-black leading-[1] mt-1"
          style={{
            fontFamily: '"Space Grotesk", sans-serif',
            color: '#E6EBF2',
            textShadow: '0 0 40px rgba(0,212,255,0.5), 0 0 80px rgba(0,212,255,0.2)',
          }}
        >
          OpenBro
        </h1>

        <div className="mt-6 mx-auto w-[200px] h-[2px] overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
          <div
            className="h-full"
            style={{
              background: '#E6EBF2',
              width: phase >= 2 ? '100%' : '0%',
              transition: 'width 1.5s ease-in-out',
              boxShadow: '0 0 10px rgba(0,212,255,0.5)',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PortalTransition;
